import { useEffect, useState, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { Tabs, Tab } from '@mui/material';
import FacilitySection from '../components/HotelInfo/FacilitySection';
import PolicySection from '../components/HotelInfo/PolicySection';
import ServiceSection from '../components/HotelInfo/ServiceSection';
import EventSection from '../components/HotelInfo/EventSection';
import RestaurantSection from '../components/HotelInfo/RestaurantSection';
import SpaSection from '../components/HotelInfo/SpaSection';
import ExploreSection from '../components/HotelInfo/ExploreSection';
import SmartPromotionSection from '../components/HotelInfo/SmartPromotionSection';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { API_BASE } from '../api/config';
import {
  fetchExplorePosts,
  createExplorePost,
  updateExplorePost,
  deleteExplorePost,
} from '../api/exploreApi';

export default function HotelInfoManagerV2({ propertyId, propertyName, allowedFeatures }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const STORAGE_KEY_TAB = 'hotelInfoManagerV2.tabIndex';
  const initialTabFromUrl = parseInt(searchParams.get('tab'), 10);
  let initialTabFromStorage = null;
  try {
    const stored = localStorage.getItem(STORAGE_KEY_TAB);
    if (stored !== null) initialTabFromStorage = parseInt(stored, 10);
  } catch {}
  const resolvedInitialTab = !Number.isNaN(initialTabFromUrl)
    ? initialTabFromUrl
    : (Number.isInteger(initialTabFromStorage) ? initialTabFromStorage : 0);
  const [tabIndex, setTabIndex] = useState(resolvedInitialTab);

  useEffect(() => {
    const tabFromUrl = parseInt(searchParams.get('tab'), 10);
    if (!Number.isNaN(tabFromUrl)) {
      setTabIndex(tabFromUrl);
    }
  }, [searchParams]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (parseInt(params.get('tab'), 10) !== tabIndex) {
      params.set('tab', tabIndex);
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    }
    try {
      localStorage.setItem(STORAGE_KEY_TAB, String(tabIndex));
    } catch {}
  }, [tabIndex, location.pathname, location.search, navigate]);
  const [sections, setSections] = useState({
    facilities: [],
    policies: [],
    services: [],
    events: [],
    restaurants: [],
    spa: [],
    explore: [],
    smartPromotions: []
  });

  const [form, setForm] = useState({});
  const [open, setOpen] = useState(false);
  const [currentType, setCurrentType] = useState('');
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [facilityDetailsOpen, setFacilityDetailsOpen] = useState(false);

  const [promotionError, setPromotionError] = useState(null);

  const fetchEntries = useCallback(async (type) => {
    const propertyIdString = propertyId?.id || propertyId;
    if (!propertyIdString) return;
    try {
      if (type === 'smartPromotions') {
        setPromotionError(null);
        const url = `${API_BASE}/smart-promotions?propertyId=${propertyIdString}&channel=inApp&includeAll=true`;
        console.log('[DEBUG] fetchSmartPromotions URL:', url);
        const res = await fetch(url);
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data?.error || 'Failed to fetch smart promotions');
        }
        setSections((prev) => ({ ...prev, smartPromotions: data.items || [] }));
        return;
      }

      if (type === 'explore') {
        const posts = await fetchExplorePosts({ propertyId: propertyIdString });
        setSections((prev) => ({ ...prev, explore: posts }));
        return;
      }
      const url = `${API_BASE}/${type}?propertyId=${propertyIdString}`;
      console.log('[DEBUG] fetchEntries URL:', url);
      const res = await fetch(url);
      const data = await res.json();
      setSections((prev) => ({ ...prev, [type]: data.data || data }));
    } catch (err) {
      console.error(`[❌ fetchEntries] Failed for ${type}`, err);
      if (type === 'smartPromotions') {
        setPromotionError(err?.message || 'Failed to load smart promotions');
      }
    }
  }, [propertyId]);

  const fetchAll = useCallback(() => {
    console.log('[DEBUG] fetchAll called for propertyId:', propertyId);
    ['facilities', 'policies', 'services', 'events', 'restaurants', 'spa', 'explore', 'smartPromotions'].forEach(fetchEntries);
  }, [propertyId, fetchEntries]);

  useEffect(() => {
    if (propertyId) fetchAll();
  }, [propertyId, fetchAll]);

  useEffect(() => {
    const allEmpty = Object.values(sections).every(list => Array.isArray(list) && list.length === 0);
    if (propertyId && allEmpty) {
      fetchAll();
    }
  }, [propertyId, sections, fetchAll]);

  useEffect(() => {
    const sectionKeys = ['facilities', 'policies', 'services', 'events', 'restaurants', 'spa', 'explore', 'smartPromotions'];
    if (propertyId) {
      const currentSection = sectionKeys[tabIndex];
      if (currentSection) {
        fetchEntries(currentSection);
      }
    }
  }, [tabIndex, propertyId, fetchEntries]);

  // handleEdit: strictly responsible for opening modal and pre-filling the form
  const handleEdit = (entry, type) => {
    setForm(entry);
    setCurrentType(type);
    setOpen(true);
  };

  const handleSubmit = async (data, type) => {
    const sectionType = type || currentType;
    if (!sectionType) {
      console.warn('[⚠️ Missing section type]');
      return { success: false };
    }

    const isNew = !data._id;
    const propertyIdString = propertyId?.id || propertyId;
    const payload = { ...data, propertyId: data.propertyId || propertyIdString };

    try {
      if (sectionType === 'explore') {
        if (isNew) {
          await createExplorePost(payload);
        } else {
          await updateExplorePost(payload._id, payload);
        }
        await fetchEntries(sectionType);
        setOpen(false);
        return { success: true };
      }

      const method = isNew ? 'POST' : 'PUT';
      let url = `${API_BASE}/${sectionType}`;
      if (!isNew) {
        url += `/${payload._id}`;
      }
      console.log(`[DEBUG] handleSubmit URL: ${method} ${url}`, 'payload:', payload);
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const resData = await res.json();
      if (!res.ok) {
        console.warn('[❌ Save Failed]', resData);
        return { success: false };
      }
      await fetchEntries(sectionType);
      setOpen(false);
      return { success: true };
    } catch (err) {
      console.error('[❌ Save Error]', err);
      return { success: false };
    }
  };

  const handleDelete = async (id, type) => {
    try {
      if (type === 'explore') {
        await deleteExplorePost(id);
      } else {
        const propertyIdString = propertyId?.id || propertyId;
        const url = `${API_BASE}/${type}/${id}?propertyId=${propertyIdString}`;
        console.log('[DEBUG] handleDelete URL:', url);
        const res = await fetch(url, { method: 'DELETE' });
        if (!res.ok) {
          const msg = await res.text();
          console.warn('[❌ Delete Failed]', msg);
          return;
        }
      }
      fetchEntries(type);
      setOpen(false); // Close modal if open during deletion
    } catch (err) {
      console.error('[❌ Delete Error]', err);
    }
  };

  const sectionProps = (type) => ({
    entries: sections[type],
    setEntries: (data) => setSections(prev => ({ ...prev, [type]: data })),
    form,
    setForm,
    open: currentType === type && open,
    setOpen: (val) => {
      if (val) setCurrentType(type);
      setOpen(val);
    },
    handleEdit: (entry) => handleEdit(entry, type),
    handleSubmitEdit: (data, submitType) => handleSubmit(data, submitType || type),
    handleDelete: (id) => handleDelete(id, type),
    propertyId,
    allowedFeatures,
    fetchEntries: () => fetchEntries(type),
    selectedFacility,
    setSelectedFacility,
    facilityDetailsOpen,
    setFacilityDetailsOpen
  });

  return (
    <Box>
      {console && console.log && console.log('[HotelInfoManagerV2] allowedFeatures prop:', allowedFeatures)}

      <Tabs
        value={tabIndex}
        onChange={(e, newVal) => setTabIndex(newVal)}
        indicatorColor="primary"
        textColor="primary"
        variant="scrollable"
        scrollButtons="auto"
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Facilities" />
        <Tab label="Policies" />
        <Tab label="Services" />
        <Tab label="Events" />
        <Tab label="Restaurants" />
        <Tab label="Spa" />
        <Tab label="Explore" />
        <Tab label="Smart Promotions" />
      </Tabs>

      <Box mt={0} sx={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
        {tabIndex === 0 && <FacilitySection sectionType="facilities" section="facilities" {...sectionProps('facilities')} />}
        {tabIndex === 1 && <PolicySection sectionType="policies" section="policies" {...sectionProps('policies')} />}
        {tabIndex === 2 && <ServiceSection sectionType="services" section="services" {...sectionProps('services')} />}
        {tabIndex === 3 && <EventSection sectionType="events" section="events" {...sectionProps('events')} />}
        {tabIndex === 4 && <RestaurantSection sectionType="restaurants" section="restaurants" fromTab="4" {...sectionProps('restaurants')} />}
        {tabIndex === 5 && <SpaSection sectionType="spa" section="spa" fromTab="5" {...sectionProps('spa')} />}
        {tabIndex === 6 && <ExploreSection sectionType="explore" {...sectionProps('explore')} />}
        {tabIndex === 7 && (
          <SmartPromotionSection
            entries={sections.smartPromotions}
            fetchEntries={() => fetchEntries('smartPromotions')}
            propertyId={propertyId}
            propertyName={propertyName}
            error={promotionError}
            services={sections.services}
            restaurants={sections.restaurants}
            spas={sections.spa}
          />
        )}
      </Box>
    </Box>
  );
}
